import { useTranslation } from "react-i18next";
import { Sun, Moon, Globe, User, MapPin } from "lucide-react";
import { useEffect, useState, useRef } from "react";
import NavbarItems from "./NavbarItems";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { logout } from "../../redux/slices/authSlice";
import { AVAILABLE_LANGS } from "../../locales/languages";
import {
  setLoginModalState,
  setSignupModalState,
} from "../../redux/slices/popupModalSlices";
import { logoutCall } from "../../services/authService";
import {
  fetchCities,
  setCitySearchQuery,
  setCoordinates,
  setSelectedCity,
} from "../../redux/slices/citySlice";
import { City } from "../../types/city.types";
import eventBus from "../../utils/eventBus";
import { LOCATION_CHANGED } from "../../constants/events";

const navbarTabItems = [
  {
    id: "home",
    href: "/",
  },
  {
    id: "dashboard",
    href: "/dashboard",
  },
  {
    id: "wash",
    href: "/wash",
  },
  {
    id: "products",
    href: "/products",
  },
  {
    id: "service",
    href: "/service",
  },
];

function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const userPopupRef = useRef<HTMLDivElement>(null);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const locationPanelRef = useRef<HTMLDivElement>(null);
  const locationButtonRef = useRef<HTMLDivElement>(null);
  const auth = useSelector((state: RootState) => state?.auth);
  const {
    availableCities,
    selectedCity,
    citySearchQuery,
    latitude,
    longitude,
  } = useSelector((state: RootState) => state.city);

  const [darkMode, setDarkMode] = useState(
    localStorage.getItem("theme") === "dark"
  );
  const [activeTabItem, setActiveTabItem] = useState("");
  const [isUserPopupOpen, setIsUserPopupOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isLocationPanelOpen, setIsLocationPanelOpen] = useState(false);
  const [showLocationHover, setShowLocationHover] = useState(false);

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsLangDropdownOpen(false);
  };

  useEffect(() => {
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        isLocationPanelOpen &&
        locationPanelRef.current &&
        !locationPanelRef.current.contains(target) &&
        locationButtonRef.current &&
        !locationButtonRef.current.contains(target)
      ) {
        setIsLocationPanelOpen(false);
      }

      if (
        isUserPopupOpen &&
        userPopupRef.current &&
        !userPopupRef.current.contains(target)
      ) {
        setIsUserPopupOpen(false);
      }

      if (
        isLangDropdownOpen &&
        langDropdownRef.current &&
        !langDropdownRef.current.contains(target)
      ) {
        setIsLangDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleGlobalClick);
    return () => document.removeEventListener("mousedown", handleGlobalClick);
  }, [isUserPopupOpen, isLangDropdownOpen, isLocationPanelOpen]);

  useEffect(() => {
    const isValidRoute = navbarTabItems.some(
      (item) =>
        location.pathname === item.href ||
        location.pathname.startsWith(item.href + "/") ||
        location.pathname === "/profile"
    );

    if (!isValidRoute) {
      window.location.href = "/";
    }
  }, [location.pathname]);

  useEffect(() => {
    const matchingTab = navbarTabItems.find(
      (tab) => tab.href === location?.pathname
    );

    if (matchingTab) {
      setActiveTabItem(matchingTab.id);
    } else if (location?.pathname === "/profile") {
      setActiveTabItem("");
    } else {
      setActiveTabItem("home");
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!latitude && !longitude && navigator.geolocation) {
      const success = (position: GeolocationPosition) => {
        const { latitude, longitude } = position.coords;
        dispatch(setCoordinates({ latitude, longitude }));
      };

      const failure = (err: GeolocationPositionError) => {
        console.error("Error obtaining location:", err.message);
      };

      navigator.geolocation.getCurrentPosition(success, failure);
    }

    if (
      !citySearchQuery.trim() &&
      !latitude &&
      !longitude &&
      !navigator.geolocation
    ) {
      dispatch(
        fetchCities({
          search: citySearchQuery,
          lat: latitude || 0,
          lng: longitude || 0,
          selectedCity,
        }) as any
      );
      return;
    }

    const delay = setTimeout(() => {
      dispatch(
        fetchCities({
          search: citySearchQuery,
          lat: latitude || 0,
          lng: longitude || 0,
          selectedCity,
        }) as any
      );
    }, 300);

    return () => clearTimeout(delay);
  }, [citySearchQuery, latitude, longitude]);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    if (darkMode) {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    } else {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    }
  };

  const handleLocationChange = (city: City) => {
    dispatch(setSelectedCity(city));
    setIsLocationPanelOpen(false);
    eventBus.emit(LOCATION_CHANGED, { city });
  };

  const handleActiveTabChange = (activeTab: string) => {
    setActiveTabItem(activeTab);
  };

  const handleUserClick = () => {
    setIsUserPopupOpen((prev) => !prev);
  };

  const handleLogin = () => {
    setIsUserPopupOpen(false);
    dispatch(setLoginModalState(true));
  };

  const handleSignup = () => {
    setIsUserPopupOpen(false);
    dispatch(setSignupModalState(true));
  };

  const handleLogout = () => {
    setIsUserPopupOpen(false);
    dispatch(logout());
    logoutCall();
  };

  return (
    <nav className="bg-primaryBg dark:bg-primaryBgDark">
      <div className="relative mx-auto py-4 px-4 flex items-center">
        <Link
          to="/"
          onClick={() => setActiveTabItem("home")}
          className="flex shrink-0 text-xl dark:text-primaryTextDark text-primaryText font-bold"
        >
          {t("appName")}
        </Link>
        <div className="hidden md:flex space-x-6 justify-center items-center absolute left-1/2 -translate-x-1/2">
          {navbarTabItems.map((navbarTabItem) => (
            <NavbarItems
              key={navbarTabItem.id}
              navbarTabItem={navbarTabItem}
              activeTabItem={activeTabItem}
              handleActiveTabChange={handleActiveTabChange}
            />
          ))}
        </div>
        <div className="ml-auto flex items-center space-x-3 relative">
          <div className="relative" ref={locationButtonRef}>
            <MapPin
              size={17}
              className="cursor-pointer dark:text-primaryTextDark text-primaryText"
              onClick={() => {
                if (!availableCities.length) return;
                setIsLocationPanelOpen((prev) => !prev);
              }}
              onMouseEnter={() => {
                !isLocationPanelOpen && setShowLocationHover(true);
              }}
              onMouseLeave={() => setShowLocationHover(false)}
            />

            {showLocationHover && selectedCity?.city && (
              <div className="absolute top-6 left-1/2 -translate-x-1/2 dark:text-primaryTextDark text-primaryText bg-popupLight dark:bg-popupDark text-xs px-2 py-1 rounded shadow-md z-40 whitespace-nowrap">
                <strong>{selectedCity?.city}</strong>
              </div>
            )}

            {isLocationPanelOpen && (
              <div
                ref={locationPanelRef}
                className="absolute right-0 mt-2 w-64 bg-popupLight dark:bg-popupDark shadow-2xl rounded-md p-3 z-50"
              >
                <h3 className="text-sm mb-2 font-semibold dark:text-primaryTextDark text-primaryText">
                  {t("Select Location")}
                </h3>

                <input
                  type="text"
                  placeholder={`${t("Search city")}...`}
                  value={citySearchQuery}
                  autoFocus={true}
                  onChange={(e) => dispatch(setCitySearchQuery(e.target.value))}
                  className="w-full px-3 py-2 rounded bg-gray-200 dark:bg-gray-700 mb-3 text-sm outline-none text-primaryText dark:text-primaryTextDark"
                />

                <div className="max-h-40 overflow-y-auto custom-scroll">
                  {availableCities.map((city) => (
                    <button
                      key={city._id}
                      onClick={() => handleLocationChange(city)}
                      className={`w-full text-left px-2 py-1 text-sm rounded flex justify-between items-center hover:bg-gray-200 dark:hover:bg-gray-700 text-primaryText dark:text-primaryTextDark`}
                    >
                      {city.city}, {city.country}
                      {city.city === selectedCity?.city && (
                        <span className="text-green-600 dark:text-green-400 text-xs">
                          ✓
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="relative" ref={langDropdownRef}>
            <Globe
              className="cursor-pointer dark:text-primaryTextDark text-primaryText"
              size={17}
              onClick={() => setIsLangDropdownOpen((prev) => !prev)}
            />
            {isLangDropdownOpen && (
              <div className="absolute right-0 mt-2 w-36 max-h-48 overflow-y-auto bg-popupLight dark:bg-popupDark shadow-lg rounded-md p-2 z-50 custom-scroll">
                {AVAILABLE_LANGS.map((lang) => (
                  <button
                    key={lang.id}
                    onClick={() => handleLanguageChange(lang.id)}
                    className={`w-full text-left px-3 py-1 text-sm rounded flex justify-between items-center ${
                      i18n.language === lang.id
                        ? "bg-gray-200 dark:bg-gray-700 font-medium"
                        : ""
                    } text-primaryText dark:text-primaryTextDark`}
                  >
                    {lang.name}
                    {i18n.language === lang.id && (
                      <span className="text-xs text-green-600 dark:text-green-400">
                        ✓
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          {darkMode ? (
            <Sun
              className="cursor-pointer dark:text-primaryTextDark text-primaryText"
              size={17}
              onClick={toggleTheme}
            />
          ) : (
            <Moon
              className="cursor-pointer dark:text-primaryTextDark text-primaryText"
              size={17}
              onClick={toggleTheme}
            />
          )}
          <div ref={userPopupRef} className="relative">
            <User
              className="cursor-pointer dark:text-primaryTextDark text-primaryText"
              size={17}
              onClick={handleUserClick}
            />
            {isUserPopupOpen && (
              <div className="absolute right-0 mt-2 w-44 bg-popupLight dark:bg-popupDark shadow-2xl rounded-md p-3 flex flex-col gap-2 z-50">
                {auth?.token && auth?.user?.name ? (
                  <>
                    <button
                      onClick={() => {
                        setIsUserPopupOpen(false);
                        navigate("/profile");
                      }}
                      className="w-full py-1.5 px-3 bg-popupLight dark:bg-popupDark text-sm rounded shadow-xl transition dark:text-primaryTextDark text-primaryText"
                    >
                      {t("Profile")}
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full py-1.5 px-3 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition"
                    >
                      {t("logout")}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleLogin}
                      className="w-full py-1.5 px-3 bg-secondaryText dark:bg-secondaryTextDark text-primaryText text-sm rounded hover:bg-blue-700 transition"
                    >
                      {t("login")}
                    </button>
                    <button
                      onClick={handleSignup}
                      className="w-full py-1.5 px-3 bg-secondaryText dark:bg-secondaryTextDark text-primaryText text-sm rounded hover:bg-blue-700 transition"
                    >
                      {t("signup")}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
